import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Typography, Button, Space, Card } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { feedbackService } from '@/services/feedbackService'
import { FeedbackTable } from '@/components/feedback/FeedbackTable'
import { FeedbackFilters, FeedbackFilterState } from '@/components/feedback/FeedbackFilters'
import FeedbackModal from '@/components/feedback/FeedbackModal'

const { Title, Text } = Typography

// Clean feedback ID search string (removes FB- prefix if present)
const cleanFeedbackIdSearch = (idParam: string | null | undefined): string | null => {
  if (!idParam) return null
  return idParam.toUpperCase().replace('FB-', '').trim()
}

export default function MyFeedbackPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const idFromUrl = searchParams.get('id')

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)

  // Initialize filters with ID from URL if present
  const [filters, setFilters] = useState<FeedbackFilterState>(() => ({
    statuses: idFromUrl ? [] : ['New', 'In Progress'],
    dateRange: null,
    feedbackId: idFromUrl || undefined,
  }))

  // Sync URL param to filter state when URL changes
  useEffect(() => {
    if (idFromUrl && filters.feedbackId !== idFromUrl) {
      setFilters(prev => ({
        ...prev,
        feedbackId: idFromUrl,
        statuses: []  // Clear status filter when filtering by ID
      }))
    }
  }, [idFromUrl])

  // Handle filter changes - sync feedbackId to URL
  const handleFilterChange = useCallback((newFilters: FeedbackFilterState) => {
    setFilters(newFilters)

    // Sync feedbackId to URL
    if (newFilters.feedbackId) {
      searchParams.set('id', newFilters.feedbackId)
      setSearchParams(searchParams, { replace: true })
    } else if (searchParams.has('id')) {
      searchParams.delete('id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Get the cleaned search string for filtering (contains search)
  const feedbackIdSearch = cleanFeedbackIdSearch(filters.feedbackId)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['feedback', filters],
    queryFn: async () => {
      // Transform FeedbackFilterState to service filter format
      const serviceFilters = {
        types: filters.types,
        statuses: filters.statuses,
        priorities: filters.priorities,
        categories: filters.categories,
        dateFrom: filters.dateRange?.[0],
        dateTo: filters.dateRange?.[1],
        search: filters.search,
        orgId: filters.orgId,
      }
      const response = await feedbackService.listFeedback(serviceFilters)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch feedback')
      }
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Filter data by feedbackId if filter is present (contains search)
  const filteredData = useMemo(() => {
    if (!data) return []
    if (!feedbackIdSearch) return data
    // Contains search: check if the feedback ID contains the search string
    return data.filter(f => f.feedbackId?.toString().includes(feedbackIdSearch))
  }, [data, feedbackIdSearch])

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>My Feedback</Title>
            <Text type="secondary">View and track your feature requests, bug reports, and feedback</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setFeedbackModalOpen(true)}>
              Submit New
            </Button>
          </Space>
        </div>

        <Card>
          <FeedbackFilters
            isAdminView={false}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </Card>

        <FeedbackTable
          data={filteredData}
          isAdminView={false}
          loading={isLoading}
          onRefresh={() => refetch()}
        />
      </Space>

      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
    </div>
  )
}
